/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */
const LOCAL_RELAY_SERVER_URL: string =
  process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';
const API_BASE_URL = 'https://api.helper.qa.tdtglobal.io/api';
//const API_BASE_URL = 'http://localhost:8000/api';

import { useRef, useCallback, useState } from 'react';

import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { instructions } from '../utils/conversation_config.js';
import { WavRenderer } from '../utils/wav_renderer';

import { X, Edit, Zap, ArrowUp, ArrowDown } from 'react-feather';
import { Button } from '../components/button/Button';
import { Toggle } from '../components/toggle/Toggle';
import { Map } from '../components/Map';
import React, { useEffect } from 'react';

import './ConsolePage.scss';
import { isJsxOpeningLikeElement } from 'typescript';
let cachedToken: string | null = null; // Tipo explícito: puede ser un string o null


async function authenticate() {
  const email = 'malvarez@tolber.io';
  const password = 'manuel.Alvarez564';

  const formData = new FormData();
  formData.append('email', email);
  formData.append('password', password);

  try {
    const loginResponse = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      body: formData,
    });

    if (!loginResponse.ok) {
      throw new Error('Error al iniciar sesión');
    }

    const data = await loginResponse.json();
    if (data.token) {
      localStorage.setItem('authToken', data.token); // Almacena el token en el almacenamiento local
      return data.token;
    } else {
      throw new Error('Token no recibido');
    }
  } catch (error) {
    console.error('Error de autenticación:', error);
  }
}




/**
 * Type for result from get_weather() function call
 */
interface Coordinates {
  lat: number;
  lng: number;
  location?: string;
  temperature?: {
    value: number;
    units: string;
  };
  wind_speed?: {
    value: number;
    units: string;
  };
}

/**
 * Type for all event logs
 */
interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

const BComponent: React.FC = () => {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'clickButton') {
        const button = document.querySelector(`#${event.data.buttonId}`) as HTMLButtonElement;
        if (button) {
          button.click();
        } else {
          console.error(`Button with id "${event.data.buttonId}" not found.`);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // No necesitas renderizar nada en este componente.
  return null;
};
export { BComponent }; // Exportación correcta

const MessageListener = () => {
  useEffect(() => {
    // Definir el manejador del evento
    const handleMessage = (event: MessageEvent) => {
      console.log('Mensaje recibido:', event.data);
    };

    // Agregar el listener al montar el componente
    window.addEventListener('message', handleMessage);

    // Limpiar el listener al desmontar el componente
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []); // [] asegura que esto se ejecute solo al montar y desmontar el componente

  return <div>Escuchando mensajes...</div>;
};

export default MessageListener;

export function ConsolePage() {
  /**
   * Ask user for API Key
   * If we're using the local relay server, we don't need this
   */
  const apiKey = LOCAL_RELAY_SERVER_URL
    ? ''
    : localStorage.getItem('tmp::voice_api_key') ||
    prompt('OpenAI API Key') ||
    '';
  if (apiKey !== '') {
    localStorage.setItem('tmp::voice_api_key', apiKey);
  }

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 })
  );
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 })
  );
  const clientRef = useRef<RealtimeClient>(
    new RealtimeClient(
      LOCAL_RELAY_SERVER_URL
        ? { url: LOCAL_RELAY_SERVER_URL }
        : {
          apiKey: apiKey,
          dangerouslyAllowAPIKeyInBrowser: true,
        }
    )
  );

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  const eventsScrollHeightRef = useRef(0);
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  const [expandedEvents, setExpandedEvents] = useState<{
    [key: string]: boolean;
  }>({});
  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [memoryKv, setMemoryKv] = useState<{ [key: string]: any }>({});
  const [coords, setCoords] = useState<Coordinates | null>({
    lat: 37.775593,
    lng: -122.418137,
  });
  const [marker, setMarker] = useState<Coordinates | null>(null);

  /**
   * Utility for formatting the timing of logs
   */
  const formatTime = useCallback((timestamp: string) => {
    const startTime = startTimeRef.current;
    const t0 = new Date(startTime).valueOf();
    const t1 = new Date(timestamp).valueOf();
    const delta = t1 - t0;
    const hs = Math.floor(delta / 10) % 100;
    const s = Math.floor(delta / 1000) % 60;
    const m = Math.floor(delta / 60_000) % 60;
    const pad = (n: number) => {
      let s = n + '';
      while (s.length < 2) {
        s = '0' + s;
      }
      return s;
    };
    return `${pad(m)}:${pad(s)}.${pad(hs)}`;
  }, []);

  /**
   * When you click the API key
   */
  const resetAPIKey = useCallback(() => {
    const apiKey = prompt('OpenAI API Key');
    if (apiKey !== null) {
      localStorage.clear();
      localStorage.setItem('tmp::voice_api_key', apiKey);
      window.location.reload();
    }
  }, []);

  /**
   * Connect to conversation:
   * WavRecorder taks speech input, WavStreamPlayer output, client is API client
   */
  const connectConversation = useCallback(async () => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // Set state variables
    startTimeRef.current = new Date().toISOString();
    setIsConnected(true);
    setRealtimeEvents([]);
    setItems(client.conversation.getItems());

    // Connect to microphone
    await wavRecorder.begin();

    // Connect to audio output
    await wavStreamPlayer.connect();

    // Connect to realtime API
    await client.connect();
    client.sendUserMessageContent([
      {
        type: `input_text`,
        text: `Hola como puedo ayudarte!`,
        // text: `For testing purposes, I want you to list ten car brands. Number each item, e.g. "one (or whatever number you are one): the item name".`
      },
    ]);

    if (client.getTurnDetectionType() === 'server_vad') {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
  }, []);

  /**
   * Disconnect and reset conversation state
   */
  const disconnectConversation = useCallback(async () => {
    setIsConnected(false);
    setRealtimeEvents([]);
    setItems([]);
    setMemoryKv({});
    setCoords({
      lat: 37.775593,
      lng: -122.418137,
    });
    setMarker(null);

    const client = clientRef.current;
    client.disconnect();

    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.end();

    const wavStreamPlayer = wavStreamPlayerRef.current;
    await wavStreamPlayer.interrupt();
  }, []);

  const deleteConversationItem = useCallback(async (id: string) => {
    const client = clientRef.current;
    client.deleteItem(id);
  }, []);

  /**
   * In push-to-talk mode, start recording
   * .appendInputAudio() for each sample
   */
  const startRecording = async () => {
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const trackSampleOffset = await wavStreamPlayer.interrupt();
    if (trackSampleOffset?.trackId) {
      const { trackId, offset } = trackSampleOffset;
      await client.cancelResponse(trackId, offset);
    }
    await wavRecorder.record((data) => client.appendInputAudio(data.mono));
  };

  /**
   * In push-to-talk mode, stop recording
   */
  const stopRecording = async () => {
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();
    client.createResponse();
  };

  /**
   * Switch between Manual <> VAD mode for communication
   */
  const changeTurnEndType = async (value: string) => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    if (value === 'none' && wavRecorder.getStatus() === 'recording') {
      await wavRecorder.pause();
    }
    client.updateSession({
      turn_detection: value === 'none' ? null : { type: 'server_vad' },
    });
    if (value === 'server_vad' && client.isConnected()) {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    }
    setCanPushToTalk(value === 'none');
  };

  /**
   * Auto-scroll the event logs
   */
  useEffect(() => {
    if (eventsScrollRef.current) {
      const eventsEl = eventsScrollRef.current;
      const scrollHeight = eventsEl.scrollHeight;
      // Only scroll if height has just changed
      if (scrollHeight !== eventsScrollHeightRef.current) {
        eventsEl.scrollTop = scrollHeight;
        eventsScrollHeightRef.current = scrollHeight;
      }
    }
  }, [realtimeEvents]);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
    }
  }, [items]);

  /**
   * Set up render loops for the visualization canvas
   */
  useEffect(() => {
    let isLoaded = true;

    const wavRecorder = wavRecorderRef.current;
    const clientCanvas = clientCanvasRef.current;
    let clientCtx: CanvasRenderingContext2D | null = null;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx: CanvasRenderingContext2D | null = null;

    const render = () => {
      if (isLoaded) {
        if (clientCanvas) {
          if (!clientCanvas.width || !clientCanvas.height) {
            clientCanvas.width = clientCanvas.offsetWidth;
            clientCanvas.height = clientCanvas.offsetHeight;
          }
          clientCtx = clientCtx || clientCanvas.getContext('2d');
          if (clientCtx) {
            clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
            const result = wavRecorder.recording
              ? wavRecorder.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              clientCanvas,
              clientCtx,
              result.values,
              '#0099ff',
              10,
              0,
              8
            );
          }
        }
        if (serverCanvas) {
          if (!serverCanvas.width || !serverCanvas.height) {
            serverCanvas.width = serverCanvas.offsetWidth;
            serverCanvas.height = serverCanvas.offsetHeight;
          }
          serverCtx = serverCtx || serverCanvas.getContext('2d');
          if (serverCtx) {
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              serverCanvas,
              serverCtx,
              result.values,
              '#009900',
              10,
              0,
              8
            );
          }
        }
        window.requestAnimationFrame(render);
      }
    };
    render();

    return () => {
      isLoaded = false;
    };
  }, []);

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   */
  useEffect(() => {
    // Get refs
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    client.updateSession({ instructions: instructions });
    // Set transcription, otherwise we don't get user transcriptions back
    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });
    // Login
    async function getAccessToken(): Promise<string> {
      if (cachedToken) {
        // Si ya tenemos el token almacenado, lo retornamos directamente
        return cachedToken;
      }
    
      const email = 'malvarez@tolber.io';
      const password = 'manuel.Alvarez564';
    
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
    
      // Login para obtener el token
      const loginResponse = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        body: formData,
      });
    
      if (!loginResponse.ok) {
        throw new Error(`Login failed: ${loginResponse.status}`);
      }
    
      const loginResult = await loginResponse.json();
    
      if (!loginResult.data?.access_token) {
        throw new Error('Login response does not contain a token');
      }
      // Almacenar el token para su uso posterior
      cachedToken = loginResult.data.access_token;
      if (!cachedToken) {
        throw new Error('Token is null or undefined');
      }
      return cachedToken;
    }
    // Add tools
    client.addTool(
      {
        name: 'set_memory',
        description: 'Saves important data about the user into memory.',
        parameters: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              description:
                'The key of the memory value. Always use lowercase and underscores, no other characters.',
            },
            value: {
              type: 'string',
              description: 'Value can be anything represented as a string',
            },
          },
          required: ['key', 'value'],
        },
      },
      async ({ key, value }: { [key: string]: any }) => {
        setMemoryKv((memoryKv) => {
          const newKv = { ...memoryKv };
          newKv[key] = value;
          return newKv;
        });
        return { ok: true };
      }
    );
    client.addTool(
      {
        name: 'get_weather',
        description:
          'Retrieves the weather for a given lat, lng coordinate pair. Specify a label for the location.',
        parameters: {
          type: 'object',
          properties: {
            lat: {
              type: 'number',
              description: 'Latitude',
            },
            lng: {
              type: 'number',
              description: 'Longitude',
            },
            location: {
              type: 'string',
              description: 'Name of the location',
            },
          },
          required: ['lat', 'lng', 'location'],
        },
      },
      async ({ lat, lng, location }: { [key: string]: any }) => {
        setMarker({ lat, lng, location });
        setCoords({ lat, lng, location });
        const result = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,wind_speed_10m`
        );
        const json = await result.json();
        const temperature = {
          value: json.current.temperature_2m as number,
          units: json.current_units.temperature_2m as string,
        };
        const wind_speed = {
          value: json.current.wind_speed_10m as number,
          units: json.current_units.wind_speed_10m as string,
        };
        setMarker({ lat, lng, location, temperature, wind_speed });
        return json;
      }
    );
    // save_ad_campaign
    client.addTool(
      {
        name: 'save_ad_campaign',
        description:
          'Saves an advertising campaign including general campaign details, country-specific data, and city-specific data to an external API.',
        parameters: {
          type: 'object',
          properties: {
            campaignName: {
              type: 'string',
              description: 'Name of the campaign',
            },
            startDay: {
              type: 'string',
              description: 'Campaign start day',
            },
            endDay: {
              type: 'string',
              description: 'Campaign end day',
            },
            briefType: {
              type: 'object',
              properties: {
                name: {
                  type: 'string',
                  description: 'Name of the brief type',
                },
                id: {
                  type: 'string',
                  description: 'ID of the brief type',
                },
              },
              required: ['name', 'id'],
              description: 'Brief type details',
            },
            campaignType: {
              type: 'object',
              properties: {
                name: {
                  type: 'string',
                  description: 'Name of the campaign type',
                },
                id: {
                  type: 'string',
                  description: 'ID of the campaign type',
                },
              },
              required: ['name', 'id'],
              description: 'Campaign type details',
            },
            targetAudience: {
              type: 'string',
              description: 'Description of the target audience',
            },
            products: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  name: {
                    type: 'string',
                    description: 'Name of the product',
                  },
                  id: {
                    type: 'string',
                    description: 'ID of the product',
                  },
                },
                required: ['name', 'id'],
              },
              description: 'List of products being advertised',
            },
            agency: {
              type: 'object',
              properties: {
                name: {
                  type: 'string',
                  description: 'Name of the agency',
                },
                id: {
                  type: 'string',
                  description: 'ID of the agency',
                },
              },
              required: ['name', 'id'],
              description: 'Agency details',
            },
            account: {
              type: 'object',
              properties: {
                name: {
                  type: 'string',
                  description: 'Name of the account',
                },
                id: {
                  type: 'string',
                  description: 'ID of the account',
                },
              },
              required: ['name', 'id'],
              description: 'Account details',
            },
            brand: {
              type: 'object',
              properties: {
                name: {
                  type: 'string',
                  description: 'Name of the brand',
                },
                id: {
                  type: 'string',
                  description: 'ID of the brand',
                },
              },
              required: ['name', 'id'],
              description: 'Brand details',
            },
            countries: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  countryName: {
                    type: 'string',
                    description: 'Name of the country',
                  },
                  cities: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        cityName: {
                          type: 'string',
                          description: 'Name of the city',
                        },
                        budget: {
                          type: 'number',
                          description: 'Budget allocated for this city',
                        },
                        currency: {
                          type: 'string',
                          description: 'Currency used for the budget',
                        },
                        pointsOfInterest: {
                          type: 'array',
                          items: {
                            type: 'string',
                          },
                          description: 'List of points of interest in the city',
                        },
                        maxDistance: {
                          type: 'number',
                          description: 'Maximum distance for the campaign in the city (in km)',
                        },
                        neighborhoods: {
                          type: 'array',
                          items: {
                            type: 'string',
                          },
                          description: 'List of neighborhoods in the city',
                        },
                      },
                      required: ['cityName', 'budget', 'currency', 'pointsOfInterest', 'maxDistance'],
                    },
                    description: 'List of cities with their specific data',
                  },
                },
                required: ['countryName', 'cities'],
              },
              description: 'List of countries with their specific data',
            },
          },
          required: ['campaignName', 'briefType', 'campaignType', 'targetAudience', 'products', 'agency', 'account', 'brand', 'countries','endDay','startDay'],
        },
      },
      async ({
        campaignName,
        briefType,
        campaignType,
        targetAudience,
        products,
        agency,
        account,
        brand,
        countries,
        endDay,
        startDay
      }: { [key: string]: any }) => {
        // Construimos el objeto de la campaña
        const campaign = {
          campaignName,
          endDay,
          startDay,
          briefType,
          campaignType,
          targetAudience,
          products,
          agency,
          account,
          brand,
          countries: countries.map((country: any) => ({
            countryName: country.countryName,
            cities: country.cities.map((city: any) => ({
              cityName: city.cityName,
              budget: city.budget,
              currency: city.currency,
              pointsOfInterest: city.pointsOfInterest,
              maxDistance: city.maxDistance,
              neighborhoods: city.neighborhoods,
            })),
          })),
          createdAt: new Date().toISOString(),
        };

        try {
          const accessToken = await getAccessToken();

          // Save campaign
          const campaignResponse = await fetch(`${API_BASE_URL}/campaigns`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ data: campaign }),
          });

          if (!campaignResponse.ok) {
            throw new Error(`API responded with status: ${campaignResponse.status}`);
          }
          const campaignResult = await campaignResponse.json();
          console.log('Campaign saved successfully:', campaignResult);

          return { message: 'Campaign saved successfully', campaignResult };
        } catch (error) {
          console.error('Error saving the campaign:', error);
          throw new Error('Unable to save the campaign');
        }
      }
    );
    //search_agency
    client.addTool(
      {
        name: 'search_agency',
        description: 'Searches for an agency by name using the provided filter and authentication.',
        parameters: {
          type: 'object',
          properties: {
            agencyName: {
              type: 'string',
              description: 'The name of the agency to search for',
            },
          },
          required: ['agencyName'],
        },
      },
      async ({ agencyName }: { [key: string]: any }) => {
        try {
          const accessToken = await getAccessToken();
    
          const searchResponse = await fetch(
            `${API_BASE_URL}/agency?filter[name]=${encodeURIComponent(agencyName)}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
    
          if (!searchResponse.ok) {
            throw new Error(`API responded with status: ${searchResponse.status}`);
          }
    
          const searchResult = await searchResponse.json();
          console.log('Agency search successful:', searchResult);
    
          return { message: 'Agency search successful', searchResult };
        } catch (error) {
          console.error('Error searching for the agency:', error);
          throw new Error('Unable to search for the agency');
        }
      }
    );
    // search_account
    client.addTool(
      {
        name: 'search_account',
        description: 'Searches for an account by name using the provided filter and authentication.',
        parameters: {
          type: 'object',
          properties: {
            accountName: {
              type: 'string',
              description: 'The name of the account to search for',
            },
          },
          required: ['accountName'],
        },
      },
      async ({ accountName }: { [key: string]: any }) => {
        try {
          const accessToken = await getAccessToken();
    
          const searchResponse = await fetch(
            `${API_BASE_URL}/accounts?filter[name]=${encodeURIComponent(accountName)}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
    
          if (!searchResponse.ok) {
            throw new Error(`API responded with status: ${searchResponse.status}`);
          }
    
          const searchResult = await searchResponse.json();
          console.log('Account search successful:', searchResult);
    
          return { message: 'Account search successful', searchResult };
        } catch (error) {
          console.error('Error searching for the account:', error);
          throw new Error('Unable to search for the account');
        }
      }
    );
    //search_brief-type
    client.addTool(
      {
        name: 'search_brief-type',
        description: 'Searches for an account by name using the provided filter and authentication.',
        parameters: {
          type: 'object',
          properties: {
            briefTypeName: {
              type: 'string',
              description: 'The name of the brief-type to search for',
            },
          },
        },
      },
      async ({ briefTypeName }: { [key: string]: any }) => {
        try {
          const accessToken = await getAccessToken(); // Obtener el token
    
          // Realizar la búsqueda usando el token almacenado
          const searchResponse = await fetch(`${API_BASE_URL}/brief-types`, {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          });
    
          if (!searchResponse.ok) {
            throw new Error(`API responded with status: ${searchResponse.status}`);
          }
    
          const searchResult = await searchResponse.json();
          console.log('brief-type search successful:', searchResult);
    
          return { message: 'brief-type search successful', searchResult };
        } catch (error) {
          console.error('Error searching for the brief-type:', error);
          throw new Error('Unable to search for the brief-type');
        }
      }
    );
    //search_campaign-objective
    client.addTool(
      {
        name: 'search_campaign-objective',
        description: 'Retrieves all campaign objectives from the API.',
        parameters: {
          type: 'object',
          properties: {},
        },
      },
      async () => {
        try {
          // Obtener el token de acceso antes de hacer la solicitud
          const accessToken = await getAccessToken();
    
          // Retrieve all campaign objectives
          const searchResponse = await fetch(`${API_BASE_URL}/campaign-objectives`, {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          });
    
          if (!searchResponse.ok) {
            throw new Error(`API responded with status: ${searchResponse.status}`);
          }
    
          const searchResult = await searchResponse.json();
          console.log('Campaign-objective retrieval successful:', searchResult);
    
          return { message: 'Campaign-objective retrieval successful', searchResult };
        } catch (error) {
          console.error('Error retrieving campaign-objectives:', error);
          throw new Error('Unable to retrieve campaign-objectives');
        }
      }
    );
    //search_product
    client.addTool(
      {
        name: 'search_product',
        description: 'Searches for products by name using the provided filter and authentication.',
        parameters: {
          type: 'object',
          properties: {
            productName: {
              type: 'string',
              description: 'The name of the product to search for',
            },
            productAccount: {
              type: 'integer',
              description: 'The id of the account',
            },
          },
          required: ['productAccount'],
        },
      },
      async ({ productAccount }: { [key: string]: any }) => {
        try {
          // Obtener el token de acceso antes de hacer la solicitud
          const accessToken = await getAccessToken();
    
          // Search products
          const searchResponse = await fetch(
            `${API_BASE_URL}/products?filter[account]=${encodeURIComponent(productAccount)}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
    
          if (!searchResponse.ok) {
            throw new Error(`API responded with status: ${searchResponse.status}`);
          }
    
          const searchResult = await searchResponse.json();
          console.log('Product search successful:', searchResult);
    
          return { message: 'Product search successful', searchResult };
        } catch (error) {
          console.error('Error searching for the product:', error);
          throw new Error('Unable to search for the product');
        }
      }
    );
    //search_brand
    client.addTool(
      {
        name: 'search_brand',
        description: 'Searches for brands by account ID using the provided filter and authentication.',
        parameters: {
          type: 'object',
          properties: {
            accountId: {
              type: 'number',
              description: 'The ID of the account to search for brands',
            },
          },
          required: ['accountId'],
        },
      },
      async ({ accountId }: { [key: string]: any }) => {
        try {
          // Obtener el token de acceso antes de hacer la solicitud
          const accessToken = await getAccessToken();
    
          // Buscar marcas por ID de cuenta
          const searchResponse = await fetch(
            `${API_BASE_URL}/brands?filter[account]=${encodeURIComponent(accountId)}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
    
          if (!searchResponse.ok) {
            throw new Error(`API responded with status: ${searchResponse.status}`);
          }
    
          const searchResult = await searchResponse.json();
          console.log('Brand search successful:', searchResult);
    
          return { message: 'Brand search successful', searchResult };
        } catch (error) {
          console.error('Error searching for the brand:', error);
          throw new Error('Unable to search for the brand');
        }
      }
    );
   
    client.addTool(
      {
        name: 'send_whatsapp_campaign',
        description: 'Sends a WhatsApp campaign using the provided campaign ID and phone numbers.',
        parameters: {
          type: 'object',
          properties: {
            phones: {
              type: 'array',
              description: 'List of phone numbers to send the campaign to',
              items: {
                type: 'string',
                description: 'Phone number in international format'
              }
            },
            campaignId: {
              type: 'number',
              description: 'The ID of the campaign to send'
            },
          },
          required: ['phones', 'campaignId'],
        },
      },
      async ({ phones, campaignId }: { phones: string[]; campaignId: number }) => {
        try {
          // Obtener el token de acceso antes de hacer la solicitud
          const accessToken = await getAccessToken();
    
          // Configurar la solicitud para enviar la campaña por WhatsApp
          const formData = new FormData();
          formData.append('campaign_id', campaignId.toString());
          phones.forEach(phone => {
            formData.append('phones[]', phone);
          });
          const response = await fetch(`${API_BASE_URL}/whatsapp/send-campaign`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`
            },
            body: formData
          });
    
          if (!response.ok) {
            throw new Error(`API responded with status: ${response.status}`);
          }
    
          const result = await response.json();
          console.log('WhatsApp campaign sent successfully:', result);
    
          return { message: 'WhatsApp campaign sent successfully', result };
        } catch (error) {
          console.error('Error sending WhatsApp campaign:', error);
          throw new Error('Unable to send WhatsApp campaign');
        }
      }
    );

    client.addTool(
      {
        name: 'filter_contacts',
        description: 'Filters contacts using optional parameters such as name and department.',
        parameters: {
          type: 'object',
          properties: {
            name: {
              type: 'string',
              description: 'The name of the contact to filter by (optional).',
            },
            department: {
              type: 'string',
              description: 'The department of the contact to filter by (optional).',
            },
          },
          required: [], // Ambos parámetros son opcionales
        },
      },
      async ({ name, department }: { name?: string; department?: string }) => {
        try {
          // Obtener el token de acceso antes de hacer la solicitud
          const accessToken = await getAccessToken();
    
          // Construir la URL con los filtros
          const params = new URLSearchParams();
          if (name) params.append('filter[name]', name);
          if (department) params.append('filter[department]', department);
    
          const url = `${API_BASE_URL}/contacts?${params.toString()}`;
    
          // Realizar la solicitud
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
    
          if (!response.ok) {
            throw new Error(`API responded with status: ${response.status}`);
          }
    
          const result = await response.json();
          console.log('Contacts filtered successfully:', result);
    
          return { message: 'Contacts filtered successfully', result };
        } catch (error) {
          console.error('Error filtering contacts:', error);
          throw new Error('Unable to filter contacts');
        }
      }
    );
    
    
    // handle realtime events from client + server for event logging
    client.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          // if we receive multiple events in a row, aggregate them for display purposes
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });
    client.on('error', (event: any) => console.error(event));
    client.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });
    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000
        );
        item.formatted.file = wavFile;
      }
      setItems(items);
    });

    setItems(client.conversation.getItems());

    return () => {
      // cleanup; resets to defaults
      client.reset();
    };
  }, []);
  const handleMessage = (event: MessageEvent) => {
    if (event.data.type === 'connect') {
      console.log('Connecting conversation');
      connectConversation();
    } else if (event.data.type === 'disconnect') {
      disconnectConversation();
    } else if (event.data.type === 'none') {
      console.log('Changing turn end type to none');
      changeTurnEndType('none');
    } else if (event.data.type === 'server_vad') {
      console.log('Changing turn end type to server_vad');
      changeTurnEndType('server_vad');
    }
  };

  useEffect(() => {
    changeTurnEndType('server_vad');
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const itemsText = items.map((item) => {
      return {
        ...item,
        formatted: {
          transcript: item.formatted.transcript,
          text: item.formatted.text,
        },
      };
    });
    window.parent.postMessage(
      {
        type: 'log',
        data: { items: itemsText },
      },
      '*'
    );
  }, [items]);

  /**
   * Render the application
   */
  return (
    <div data-component="ConsolePage">
      <div className="content-top">
        <div className="content-title">
          <img src="/openai-logomark.svg" />
          <span>realtime console2</span>
        </div>
        <div className="content-api-key">
          {!LOCAL_RELAY_SERVER_URL && (
            <Button
              icon={Edit}
              iconPosition="end"
              buttonStyle="flush"
              label={`api key: ${apiKey.slice(0, 3)}...`}
              onClick={() => resetAPIKey()}
            />
          )}
        </div>
      </div>
      <div className="content-main">
        <div className="content-logs">
          <div className="content-block events">
            <div className="visualization">
              <div className="visualization-entry client">
                <canvas ref={clientCanvasRef} />
              </div>
              <div className="visualization-entry server">
                <canvas ref={serverCanvasRef} />
              </div>
            </div>
            <div className="content-block-title">events</div>
            <div className="content-block-body" ref={eventsScrollRef}>
              {!realtimeEvents.length && `awaiting connection...`}
              {realtimeEvents.map((realtimeEvent, i) => {
                const count = realtimeEvent.count;
                const event = { ...realtimeEvent.event };
                if (event.type === 'input_audio_buffer.append') {
                  event.audio = `[trimmed: ${event.audio.length} bytes]`;
                } else if (event.type === 'response.audio.delta') {
                  event.delta = `[trimmed: ${event.delta.length} bytes]`;
                }
                return (
                  <div className="event" key={event.event_id}>
                    <div className="event-timestamp">
                      {formatTime(realtimeEvent.time)}
                    </div>
                    <div className="event-details">
                      <div
                        className="event-summary"
                        onClick={() => {
                          // toggle event details
                          const id = event.event_id;
                          const expanded = { ...expandedEvents };
                          if (expanded[id]) {
                            delete expanded[id];
                          } else {
                            expanded[id] = true;
                          }
                          setExpandedEvents(expanded);
                        }}
                      >
                        <div
                          className={`event-source ${event.type === 'error'
                            ? 'error'
                            : realtimeEvent.source
                            }`}
                        >
                          {realtimeEvent.source === 'client' ? (
                            <ArrowUp />
                          ) : (
                            <ArrowDown />
                          )}
                          <span>
                            {event.type === 'error'
                              ? 'error!'
                              : realtimeEvent.source}
                          </span>
                        </div>
                        <div className="event-type">
                          {event.type}
                          {count && ` (${count})`}
                        </div>
                      </div>
                      {!!expandedEvents[event.event_id] && (
                        <div className="event-payload">
                          {JSON.stringify(event, null, 2)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="content-block conversation">
            <div className="content-block-title">conversation</div>
            <div className="content-block-body" data-conversation-content>
              {!items.length && `awaiting connection...`}
              {items.map((conversationItem, i) => {
                return (
                  <div className="conversation-item" key={conversationItem.id}>
                    <div className={`speaker ${conversationItem.role || ''}`}>
                      <div>
                        {(
                          conversationItem.role || conversationItem.type
                        ).replaceAll('_', ' ')}
                      </div>
                      <div
                        className="close"
                        onClick={() =>
                          deleteConversationItem(conversationItem.id)
                        }
                      >
                        <X />
                      </div>
                    </div>
                    <div className={`speaker-content`}>
                      {/* tool response */}
                      {conversationItem.type === 'function_call_output' && (
                        <div>{conversationItem.formatted.output}</div>
                      )}
                      {/* tool call */}
                      {!!conversationItem.formatted.tool && (
                        <div>
                          {conversationItem.formatted.tool.name}(
                          {conversationItem.formatted.tool.arguments})
                        </div>
                      )}
                      {!conversationItem.formatted.tool &&
                        conversationItem.role === 'user' && (
                          <div>
                            {conversationItem.formatted.transcript ||
                              (conversationItem.formatted.audio?.length
                                ? '(awaiting transcript)'
                                : conversationItem.formatted.text ||
                                '(item sent)')}
                          </div>
                        )}
                      {!conversationItem.formatted.tool &&
                        conversationItem.role === 'assistant' && (
                          <div>
                            {conversationItem.formatted.transcript ||
                              conversationItem.formatted.text ||
                              '(truncated)'}
                          </div>
                        )}
                      {conversationItem.formatted.file && (
                        <audio
                          src={conversationItem.formatted.file.url}
                          controls
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="content-actions">
          <div id="switch-id">
            <Toggle
              defaultValue={false}
              labels={['manual', 'vad']}
              values={['none', 'server_vad']}
              onChange={(_, value) => changeTurnEndType(value)}
            />
            </div>
            <div className="spacer" />
            {isConnected && canPushToTalk && (
              <Button
                label={isRecording ? 'release to send' : 'push to talk'}
                buttonStyle={isRecording ? 'alert' : 'regular'}
                disabled={!isConnected || !canPushToTalk}
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
              />
            )}
            <div className="spacer" />
            <Button
              id="conversation-button"
              label={isConnected ? 'disconnect' : 'connect'}
              iconPosition={isConnected ? 'end' : 'start'}
              icon={isConnected ? X : Zap}
              buttonStyle={isConnected ? 'regular' : 'action'}
              onClick={isConnected ? disconnectConversation : connectConversation}
            />

          </div>
        </div>
        <div className="content-right">
          <div className="content-block map">
            <div className="content-block-title">get_weather()</div>
            <div className="content-block-title bottom">
              {marker?.location || 'not yet retrieved'}
              {!!marker?.temperature && (
                <>
                  <br />
                  🌡️ {marker.temperature.value} {marker.temperature.units}
                </>
              )}
              {!!marker?.wind_speed && (
                <>
                  {' '}
                  🍃 {marker.wind_speed.value} {marker.wind_speed.units}
                </>
              )}
            </div>
            <div className="content-block-body full">
              {coords && (
                <Map
                  center={[coords.lat, coords.lng]}
                  location={coords.location}
                />
              )}
            </div>
          </div>
          <div className="content-block kv">
            <div className="content-block-title">set_memory()</div>
            <div className="content-block-body content-kv">
              {JSON.stringify(memoryKv, null, 2)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
